// aiSkills/dispatcher — Kör en ExecutionPlan genom att mappa varje steps'
// `tool`-namn mot en provided ToolImplementation och utföra dem sekventiellt
// mot ett StagingState-objekt. Dispatcher:n är ren — inga side-effects
// utanför det injicerade staging-objektet — så tester kan verifiera
// end-to-end skill-execution utan LLM/DOM/Supabase.
//
// Pipeline per step:
//   1. Lookup tool-impl via name
//   2. Kör impl(args, stagingState) → { ok, result?, error? }
//   3. Samla i StepResult med timestamp + error-detail
//   4. Early-abort vid critical error (validate_diagram med violations →
//      continue men signalera till caller)
//
// Resultat: ExecutionRunReport som UI kan visa i en stepper.

import type { SkillStep, ExecutionPlan } from '@/lib/aiSkills/skillRegistry';

export interface ToolImpl {
  /** Kör tool:en mot staging-state. Mutation av state är OK (ren inom denna scope). */
  (args: Record<string, unknown>, state: StagingState): ToolResult;
}

export interface ToolResult {
  ok: boolean;
  /** Tool-specifikt resultat (t.ex. idMap, sheetId, violations). */
  result?: unknown;
  /** Felmeddelande om ok=false. */
  error?: string;
}

/**
 * Mutable staging-state som tool-impls skriver till. En simplifierad
 * "mini-multi-sheet-workspace" som dispatcher:n äger under körningen.
 */
export interface StagingState {
  /** Sheet-id per sheet-ref (skapad av create_sheet). */
  sheetsByRef: Map<string, string>;
  /** Ackumulerade refs→block-ids per sheet (från batch_create_blocks). */
  blockIdMap: Map<string, string>;
  /** Runtime-spårning: antal skapade blocks/connectors totalt. */
  counts: { sheets: number; blocks: number; connectors: number; crossSheetLinks: number };
  /** Violations från senaste validate_diagram-call. */
  latestViolations: unknown[];
}

export interface StepResult {
  step: SkillStep;
  ok: boolean;
  result?: unknown;
  error?: string;
  durationMs: number;
}

export interface ExecutionRunReport {
  plan: ExecutionPlan;
  steps: StepResult[];
  ok: boolean;
  /** Total tool-execution-tid (exkluderar LLM-calls om de finns). */
  totalDurationMs: number;
  /** Första step som failade (ok:false). Null om alla passerade. */
  firstFailure: StepResult | null;
}

export function emptyStagingState(): StagingState {
  return {
    sheetsByRef: new Map(),
    blockIdMap: new Map(),
    counts: { sheets: 0, blocks: 0, connectors: 0, crossSheetLinks: 0 },
    latestViolations: [],
  };
}

export interface RunPlanOptions {
  /** Tool-implementationer keyed by ToolDefinition.name. */
  tools: Record<string, ToolImpl>;
  /** Klocka för deterministiska tester. Default Date.now. */
  now?: () => number;
  /** Om true: stoppa vid första failure istället för att köra vidare. */
  stopOnFailure?: boolean;
}

/**
 * Kör en ExecutionPlan. Skickar samma StagingState till varje tool-impl så
 * state byggs upp stegvis. Returnerar full run-rapport.
 */
export function runPlan(
  plan: ExecutionPlan,
  opts: RunPlanOptions,
): ExecutionRunReport {
  const state = emptyStagingState();
  const now = opts.now ?? (() => Date.now());
  const steps: StepResult[] = [];
  const runStart = now();
  let firstFailure: StepResult | null = null;

  for (const step of plan.steps) {
    const t0 = now();
    const impl = opts.tools[step.tool];
    if (!impl) {
      const sr: StepResult = {
        step, ok: false,
        error: `no-tool-impl: ${step.tool}`,
        durationMs: now() - t0,
      };
      steps.push(sr);
      if (!firstFailure) firstFailure = sr;
      if (opts.stopOnFailure) break;
      continue;
    }
    let sr: StepResult;
    try {
      const result = impl(step.args, state);
      sr = {
        step,
        ok: result.ok,
        result: result.result,
        error: result.error,
        durationMs: now() - t0,
      };
    } catch (e) {
      sr = {
        step, ok: false,
        error: (e as Error).message ?? 'unknown-error',
        durationMs: now() - t0,
      };
    }
    steps.push(sr);
    if (!sr.ok && !firstFailure) firstFailure = sr;
    if (!sr.ok && opts.stopOnFailure) break;
  }

  return {
    plan,
    steps,
    ok: steps.every(s => s.ok),
    totalDurationMs: now() - runStart,
    firstFailure,
  };
}

/**
 * Default tool-impls för diagram-authoring. Använder pure-core från
 * aiDiagramTools och skriver till det injected staging-state:t. LLM kan
 * svepa in egna impls för att t.ex. mocka validate_diagram under
 * utveckling.
 */
export { buildDefaultToolImpls } from '@/lib/aiSkills/defaultToolImpls';
